import styled from '@emotion/styled'

import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { metrics, colors } from 'component-library/__styling/settings'
import { supports } from 'component-library/__helpers/at-supports-helper'
import { themedValue } from 'component-library/__styling/themed-value'
import { JanusLayoutRatio } from './Janus'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

function layoutRatioToValue(ratio: JanusLayoutRatio, isFlex: true): [string, string]
function layoutRatioToValue(ratio: JanusLayoutRatio, isFlex: false): string
function layoutRatioToValue(ratio: JanusLayoutRatio, isFlex?: boolean): any {
    if (typeof isFlex === 'boolean' && isFlex) {
        switch (ratio) {
            case '3:1':
                return ['66.666666%', '33.333333%']
            case '1:3':
                return ['33.333333%', '66.666666%']
            default:
                return ['50%', '50%']
        }
    } else if (typeof isFlex === 'undefined' || !isFlex) {
        switch (ratio) {
            case '3:1':
                return '3fr 1fr'
            case '1:3':
                return '1fr 3fr'
            default:
                return 'repeat(2, 1fr)'
        }
    }
}

export interface Props {
    hasBackgroundFill?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    layoutRatio?: JanusLayoutRatio
}

export const StyledJanus = styled('div')<Props>(
    {
        margin: '0 auto',
        width: '100%',

        // Fallback styles
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
    },
    ({ theme, layoutRatio = '1:1' }) =>
        supports('display: grid', {
            display: 'grid',
            gridTemplateColumns: '100%',
            gridGap: calcRem(theme.margins.gridGap),

            [breakpoint('xs')]: {
                gridTemplateColumns: themedValue(theme, {
                    thewest: undefined,
                    fallback: layoutRatioToValue(layoutRatio, false),
                }),
            },

            [breakpoint('md')]: {
                gridTemplateColumns: themedValue(theme, {
                    thewest: layoutRatioToValue(layoutRatio, false),
                    fallback: undefined,
                }),
            },

            // Fallback overrides
            flexWrap: 'unset',
        }),
    props => ({
        backgroundColor: props.hasBackgroundFill ? colors.white : 'transparent',
        marginBottom:
            props.verticalSpacing && calcRem(metrics.perthnow.margins[props.verticalSpacing]),
    })
)

interface CellProps {
    grid?: boolean
    layoutRatio: JanusLayoutRatio
}
export const StyledGridItem = styled('div')<CellProps>(
    ({ theme, layoutRatio }) => {
        const [col1Dimensions, col2Dimensions] = layoutRatioToValue(layoutRatio, true)

        return {
            display: 'flex',

            // Fallback styles
            flex: '1 1 100%',
            flexDirection: 'column',

            ':nth-child(1)': {
                marginBottom: calcRem(theme.margins.gridGap),
            },

            [breakpoint('md')]: {
                flex: '1 1 auto',
                width: `calc(50% - (${calcRem(theme.margins.gridGap)} / 2))`,

                ':nth-child(1)': {
                    marginRight: calcRem(theme.margins.gridGap / 2),
                    marginBottom: 0,

                    ...conditionalStyles(layoutRatio !== '1:1', {
                        width: `calc(${col1Dimensions} - (${calcRem(theme.margins.gridGap)} / 2))`,
                    }),
                },

                ':nth-child(2)': {
                    marginLeft: calcRem(theme.margins.gridGap / 2),
                    ...conditionalStyles(layoutRatio !== '1:1', {
                        width: `calc(${col2Dimensions} - (${calcRem(theme.margins.gridGap)} / 2))`,
                    }),
                },
            },
        }
    },
    supports('display: grid', {
        justifyContent: 'flex-start',
        width: 'auto',

        // Fallback overrides
        flex: '1 1 auto',

        ':nth-child(1)': {
            marginBottom: 0,
        },

        [breakpoint('md')]: {
            ':nth-child(1)': {
                marginRight: 0,
                marginBottom: 0,
                width: 'auto',
            },

            ':nth-child(2)': {
                marginLeft: 0,
                width: 'auto',
            },
        },
    })
)
